import { useState, useCallback, useRef, useEffect } from 'react';

export type PlaybackMode = 'preview' | 'full';

interface UseAppleMusicPlayerOptions {
  /** Whether MusicKit is ready & the user is authorized for full playback */
  isAuthorized: boolean;
  /** Whether MusicKit JS has finished initialising */
  isMusicKitReady: boolean;
}

/**
 * Manages playback for the Apple Music playlist field.
 *
 * - **Authorized** users → full-length playback via MusicKit player
 * - **Unauthenticated** users → 30-second preview via ReactPlayer (existing)
 *
 * Returns one unified API so the component doesn't care which mode is active.
 */
export function useAppleMusicPlayer({ isAuthorized, isMusicKitReady }: UseAppleMusicPlayerOptions) {
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playbackMode: PlaybackMode = isAuthorized && isMusicKitReady ? 'full' : 'preview';
  const listenerRef = useRef<(() => void) | null>(null);

  // Clean up MusicKit listeners on unmount
  useEffect(() => {
    return () => {
      if (listenerRef.current) {
        try {
          const music = window.MusicKit.getInstance();
          music.removeEventListener('playbackStateDidChange', listenerRef.current);
        } catch { /* noop */ }
      }
    };
  }, []);

  // ---- Stop MusicKit when switching away from full mode ----
  useEffect(() => {
    if (playbackMode !== 'full' && playingTrackId) {
      try {
        const music = window.MusicKit.getInstance();
        music.stop();
      } catch { /* noop */ }
    }
  }, [playbackMode]);

  // ---- Play / Pause toggle ----
  const playPause = useCallback(
    async (trackId: string, previewUrl: string | null) => {
      // Same track → toggle
      if (playingTrackId === trackId) {
        if (playbackMode === 'full') {
          const music = window.MusicKit.getInstance();
          if (isPlaying) {
            music.pause();
          } else {
            await music.play();
          }
        }
        setIsPlaying((prev) => !prev);
        return;
      }

      // Different track → start new
      setPlayingTrackId(trackId);
      setIsPlaying(true);

      if (playbackMode === 'full') {
        try {
          const music = window.MusicKit.getInstance();

          // Tear down previous listener
          if (listenerRef.current) {
            music.removeEventListener('playbackStateDidChange', listenerRef.current);
          }

          // Set up state listener
          const onStateChange = () => {
            const state = music.playbackState;
            // PlaybackStates: playing=2, paused=3, ended/stopped/completed → stop
            if (state === MusicKit.PlaybackStates.playing) {
              setIsPlaying(true);
            } else if (state === MusicKit.PlaybackStates.paused) {
              setIsPlaying(false);
            } else if (
              state === MusicKit.PlaybackStates.ended ||
              state === MusicKit.PlaybackStates.stopped ||
              state === MusicKit.PlaybackStates.completed
            ) {
              setIsPlaying(false);
              setPlayingTrackId(null);
            }
          };
          listenerRef.current = onStateChange;
          music.addEventListener('playbackStateDidChange', onStateChange);

          await music.setQueue({ songs: [trackId] });
          await music.play();
        } catch (err) {
          console.error('MusicKit playback error:', err);
          // Fall back to preview if full playback fails
          setIsPlaying(previewUrl ? true : false);
          if (!previewUrl) setPlayingTrackId(null);
        }
      }
      // For preview mode the component handles ReactPlayer via the returned state
    },
    [playingTrackId, isPlaying, playbackMode]
  );

  // ---- Stop playback ----
  const stop = useCallback(() => {
    if (playbackMode === 'full') {
      try {
        window.MusicKit.getInstance().stop();
      } catch { /* noop */ }
    }
    setIsPlaying(false);
    setPlayingTrackId(null);
  }, [playbackMode]);

  // ---- Called by ReactPlayer when a preview ends ----
  const handlePreviewEnded = useCallback(() => {
    setIsPlaying(false);
    setPlayingTrackId(null);
  }, []);

  return {
    playingTrackId,
    isPlaying,
    playbackMode,
    playPause,
    stop,
    handlePreviewEnded,
  };
}
